/**
 * Depot Capacity Check: the visitor's request for a DepotTwin study.
 *
 * The page's only network call. It is made when the visitor presses Send, and
 * goes to one public, unauthenticated endpoint on the notifications service
 * (backend/services/notifications/crm/intake.py), which records the request
 * in AmpVerve's CRM for a person to answer. No token is sent and nothing is
 * emailed automatically. If the call fails, the page offers the visitor's own
 * mail client instead.
 */
import type { CapacityInputs, CapacityResult } from "./model";

export const INTAKE_PATH = "/api/notifications/intake/capacity-check";

/** Shown beside the consent box. The server records its own copy of this wording. */
export const CONSENT_TEXT =
  "I agree that AmpVerve may contact me by email about a DepotTwin study for this depot, and may keep these details for that purpose.";

export interface ContactFields {
  company: string;
  name: string;
  email: string;
  depotPostcode: string;
  consent: boolean;
  /** Honeypot. Hidden from people. */
  website: string;
}

const num = (v: string, fallback: number) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : fallback;
};
const r2 = (x: number) => Math.round(x * 100) / 100;

/** The JSON body the intake endpoint accepts. Pure, so it is tested on its own. */
export function buildRequest(
  contact: ContactFields,
  inputs: CapacityInputs,
  result: CapacityResult,
  formStartedMs: number,
) {
  const p = result.params;
  return {
    company: contact.company.trim(),
    name: contact.name.trim(),
    email: contact.email.trim(),
    depot_postcode: contact.depotPostcode.trim(),
    consent_contact: contact.consent,
    form_started_ms: Math.max(0, Math.round(formStartedMs)),
    website: contact.website,
    inputs: {
      kva: r2(num(inputs.kva, 200)),
      day_load_kw: r2(num(inputs.dayLoadKw, 60)),
      night_load_kw: r2(num(inputs.nightLoadKw, 20)),
      vans: p.n,
      arrive: inputs.arrive,
      depart: inputs.depart,
      miles_per_day: r2(num(inputs.milesPerDay, 70)),
      miles_per_kwh: r2(num(inputs.milesPerKwh, 2.5)),
      charger_kw: r2(p.kw),
      day_rate_pence: r2(num(inputs.dayRatePence, 24)),
      night_rate_pence: r2(num(inputs.nightRatePence, 12)),
      operating_days: Math.round(p.days),
      power_factor: r2(Math.min(1, Math.max(0.5, num(inputs.powerFactor, 0.95)))),
    },
    estimates: {
      connection_kw: r2(p.cap),
      vans_fit_on_arrival: result.maxOnArrival,
      vans_fit_smart: result.maxSmart,
      peak_on_arrival_kw: r2(result.onArrivalPeak),
      peak_smart_kw: r2(result.smartPeak),
      annual_saving_gbp: result.annualCostDifference === null ? null : r2(result.annualCostDifference),
      timed_peak_kw: result.timed ? r2(result.timed.peak) : null,
      too_short: result.tooShort,
    },
  };
}

export type SendOutcome =
  | { ok: true; message: string }
  /** `retry` false means the visitor should email instead. */
  | { ok: false; message: string; retry: boolean };

const FALLBACK = "We could not send that just now. Email us your results instead, using the link below.";

export async function sendRequest(body: ReturnType<typeof buildRequest>): Promise<SendOutcome> {
  let res: Response;
  try {
    res = await fetch(INTAKE_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, message: FALLBACK, retry: false };
  }
  let data: { message?: unknown; detail?: unknown } = {};
  try {
    data = await res.json();
  } catch {
    // keep the defaults
  }
  if (res.ok) {
    return { ok: true, message: typeof data.message === "string" ? data.message : "Thanks, your request has reached our team." };
  }
  if (res.status === 422 && typeof data.detail === "string") {
    return { ok: false, message: data.detail, retry: true };
  }
  return { ok: false, message: FALLBACK, retry: false };
}
